/**
 * PackagePreviewItem - Document preview with inline PDF rendering
 */

import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

import {
  DocumentIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { PDFDocumentProxy } from 'pdfjs-dist';
import * as pdfjs from 'pdfjs-dist';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';

import { cn } from '@/lib/utils';
import { useGetBlobDetailQuery } from '@/services/api/blobService';
import type { CorrespondenceComponentSchema } from '@/services/api/generated/correspondenceService';

import { getComponentTitle } from '../utils/component';
import { RenderStatusIcon } from '../utils/status';
import { normalizeToPdf } from './utils';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface Props {
  component: CorrespondenceComponentSchema;
  index: number;
  isSelected: boolean;
  matterId?: string;
  onClick: () => void;
  /** Package-level status - when package is queued/generating, components inherit that state */
  packageStatus?: string;
}

// =============================================================================
// Helpers
// =============================================================================

const Spinner = () => (
  <div className="h-[500px] flex items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-300 border-t-blue-600" />
  </div>
);

const Placeholder: React.FC<{
  icon: React.ElementType;
  title: string;
  sub?: string;
}> = ({ icon: Icon, title, sub }) => (
  <div className="h-[300px] flex flex-col items-center justify-center bg-zinc-50 rounded border border-dashed border-zinc-200">
    <Icon className="h-10 w-10 text-zinc-300 mb-3" />
    <p className="text-sm text-zinc-500">{title}</p>
    {sub && <p className="text-xs text-zinc-400 mt-1">{sub}</p>}
  </div>
);

// =============================================================================
// PDF Renderer - renders pages at container width
// =============================================================================

const PdfPages: React.FC<{ blob: Blob }> = ({ blob }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setWidth(w);
    };
    measure();
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ width: '100%' }}>
      {width > 0 && (
        <Document file={blob} onLoadSuccess={setPdf} loading={<Spinner />}>
          <div className="flex flex-col gap-6">
            {Array.from({ length: pdf?.numPages || 0 }, (_, i) => (
              <Page
                key={i}
                pageNumber={i + 1}
                width={width}
                className="shadow-xl"
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            ))}
          </div>
        </Document>
      )}
    </div>
  );
};

// =============================================================================
// URL Fetcher - fetches PDF from URL then renders
// Strips query params to avoid refetching on polling (signed URL tokens change)
// =============================================================================

const stripQueryParams = (url: string): string => {
  try {
    const u = new URL(url, window.location.origin);
    return `${u.origin}${u.pathname}`;
  } catch {
    return url;
  }
};

export const UrlPdf: React.FC<{ url: string }> = ({ url }) => {
  const fetchedRef = useRef<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const baseUrl = stripQueryParams(url);
    // Skip if we already fetched this PDF (ignore query param changes from polling)
    if (fetchedRef.current === baseUrl && blob) {
      return;
    }
    setLoading(true);
    fetch(url)
      .then((r) => r.blob())
      .then((b) => {
        setBlob(b);
        fetchedRef.current = baseUrl;
      })
      .finally(() => setLoading(false));
  }, [url, blob]);

  if (loading) return <Spinner />;
  if (!blob) return <Placeholder icon={DocumentIcon} title="Failed to load" />;
  return <PdfPages blob={blob} />;
};

// =============================================================================
// Blob Fetcher - fetches blob detail, normalizes, then renders
// =============================================================================

const BlobPdf: React.FC<{
  blobId: string;
  matterId: string;
}> = ({ blobId, matterId }) => {
  const { data, isLoading } = useGetBlobDetailQuery(
    { matterId, blobId },
    { skip: !blobId || !matterId },
  );
  const fetchedRef = useRef<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);

  useEffect(() => {
    if (!data?.download_url) return;
    const baseUrl = stripQueryParams(data.download_url);
    // Skip if already fetched this blob
    if (fetchedRef.current === baseUrl && blob) return;

    fetch(data.download_url)
      .then((r) => r.blob())
      .then(normalizeToPdf)
      .then((b) => {
        setBlob(b);
        fetchedRef.current = baseUrl;
      });
  }, [data?.download_url, blob]);

  if (isLoading) return <Spinner />;
  if (!blob) {
    return <Placeholder icon={DocumentIcon} title="Document not found" />;
  }
  return <PdfPages blob={blob} />;
};

// =============================================================================
// Content Router (memoized to prevent flicker during polling)
// =============================================================================

const Content = React.memo<{
  component: CorrespondenceComponentSchema;
  matterId?: string;
  packageStatus?: string;
}>(
  ({ component, matterId, packageStatus }) => {
    // Only show blur/spinner if component needs re-render AND package is rendering
    const packageIsRendering =
      packageStatus === 'queued_for_render' || packageStatus === 'generating';
    const showPackageStatus = component.needs_rerender && packageIsRendering;

    // Component shows queued/generating if its own status OR inherited from package
    const isQueued =
      component.status === 'queued_for_render' ||
      (showPackageStatus && packageStatus === 'queued_for_render');
    const isGenerating =
      component.status === 'generating' ||
      (showPackageStatus && packageStatus === 'generating');
    const showSpinner = isQueued || isGenerating;

    // If component already has rendered content, show it (with blur overlay if re-rendering)
    if (component.rendered_pdf_url) {
      return (
        <div className="relative">
          <div className={showSpinner ? 'blur-sm opacity-60' : ''}>
            <UrlPdf url={component.rendered_pdf_url} />
          </div>
          {showSpinner && (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div
                className={`h-8 w-8 animate-spin rounded-full border-2 border-zinc-300 ${isQueued ? 'border-t-violet-600' : 'border-t-blue-600'} mb-3`}
              />
              <p className="text-sm text-zinc-600 font-medium">
                {isQueued ? 'Queued...' : 'Generating...'}
              </p>
            </div>
          )}
        </div>
      );
    }

    if (
      component.component_type === 'matter_blob' &&
      component.blob_id &&
      matterId
    ) {
      return (
        <div className="relative">
          <div className={showSpinner ? 'blur-sm opacity-60' : ''}>
            <BlobPdf blobId={component.blob_id} matterId={matterId} />
          </div>
          {showSpinner && (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div
                className={`h-8 w-8 animate-spin rounded-full border-2 border-zinc-300 ${isQueued ? 'border-t-violet-600' : 'border-t-blue-600'} mb-3`}
              />
              <p className="text-sm text-zinc-600 font-medium">
                {isQueued ? 'Queued...' : 'Generating...'}
              </p>
            </div>
          )}
        </div>
      );
    }

    // No rendered content yet - show spinner or placeholder

    if (isQueued) {
      return (
        <div className="h-[300px] flex flex-col items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-300 border-t-violet-600 mb-3" />
          <p className="text-sm text-zinc-500">Queued...</p>
        </div>
      );
    }

    if (isGenerating) {
      return (
        <div className="h-[300px] flex flex-col items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-300 border-t-blue-600 mb-3" />
          <p className="text-sm text-zinc-500">Generating...</p>
        </div>
      );
    }

    if (component.status === 'error') {
      return (
        <Placeholder
          icon={ExclamationTriangleIcon}
          title="Failed"
          sub={component.error_message || undefined}
        />
      );
    }

    // No content and not processing - show placeholder
    return (
      <Placeholder
        icon={
          component.component_type === 'template'
            ? DocumentTextIcon
            : DocumentIcon
        }
        title={
          component.template?.display_name ||
          component.template?.name ||
          'Document'
        }
        sub="Generate to preview"
      />
    );
  },
  (prev, next) => {
    // Only re-render if meaningful data changed (ignore query param changes)
    if (prev.component.status !== next.component.status) return false;
    if (prev.component.id !== next.component.id) return false;
    if (prev.component.needs_rerender !== next.component.needs_rerender)
      return false;
    if (prev.matterId !== next.matterId) return false;
    if (prev.packageStatus !== next.packageStatus) return false;
    const prevUrl = prev.component.rendered_pdf_url;
    const nextUrl = next.component.rendered_pdf_url;
    if (!prevUrl && !nextUrl) return true;
    if (!prevUrl || !nextUrl) return false;
    return stripQueryParams(prevUrl) === stripQueryParams(nextUrl);
  },
);

// =============================================================================
// Main Export
// =============================================================================

export const PackagePreviewItem: React.FC<Props> = ({
  component,
  index,
  isSelected,
  matterId,
  onClick,
  packageStatus,
}) => {
  // Effective status: inherit package-level queued/generating state
  const effectiveStatus =
    packageStatus === 'queued_for_render' || packageStatus === 'generating'
      ? packageStatus
      : component.status;

  return (
    <div
      className={cn(
        'w-full',
        isSelected && 'ring-2 ring-blue-500 ring-offset-4 rounded',
      )}
    >
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'w-full text-left flex items-center gap-2 px-1 py-1 mb-2 rounded text-sm font-medium',
          'hover:bg-zinc-100 transition-colors',
          isSelected ? 'text-blue-600' : 'text-zinc-700',
        )}
      >
        <span className="w-5 h-5 rounded-full bg-zinc-200 text-zinc-500 text-[10px] font-bold flex items-center justify-center">
          {index + 1}
        </span>
        <span className="flex-1 truncate">{getComponentTitle(component)}</span>
        <RenderStatusIcon status={effectiveStatus || 'draft'} size="sm" />
      </button>
      <Content
        component={component}
        matterId={matterId}
        packageStatus={packageStatus}
      />
    </div>
  );
};
